import { NotificationGroup, Root, TargetFieldRef } from "@aidkitorg/types/lib/survey";
import {
  DependencyMap,
  DependencyNode,
  DistroComponent,
  TRANSLATORS,
} from "@aidkitorg/types/lib/translation/questions";

export function getDependencyMap(survey: Root): DependencyMap {
  const map = new Map<TargetFieldRef, DependencyNode>();
  console.time("getDependencyMap elapsed time:");
  ((Array.isArray(survey) ? survey : survey?.survey) ?? []).forEach(
    (collectionComponent) => {
      TRANSLATORS[collectionComponent.kind].calculateDependencies(
        collectionComponent as any,
        map
      );
    }
  );
  console.timeEnd("getDependencyMap elapsed time:");
  return map;
}

/**
 * Collects and returns any NotificationGroup notifs that depend (including transitively) on the provided targetField.
 * Only considers "formula" type dependencies, not content or conditionals.
 */
export function findAnyDownstreamNotifications(
  map: DependencyMap,
  startId: TargetFieldRef
) {
  // Nodes/fields we've seen already
  const visited = new Set<TargetFieldRef>();
  const downstreamNotifications: NotificationGroup[] = [];
  // Stack for depth-first-search
  const stack = [startId];
  while (stack.length > 0) {
    const currentId = stack.pop()!;
    // Skip if we’ve already visited
    if (!currentId || visited.has(currentId)) {
      continue;
    }
    visited.add(currentId);
    const currentNode = map.get(currentId);
    if (!currentNode) {
      continue;
    }
    if (
      currentNode.component &&
      currentNode.component.kind === "Notification"
    ) {
      downstreamNotifications.push(currentNode.component);
    }
    for (const children of currentNode.children) {
      if (children.type !== "formula") continue;
      // This likely indicates a misconfiguration in Distro
      if (!children.field) {
        const errorMsg = `Empty reference of type "${children.type}" found for ${currentId}${currentNode.component ? ' kind: "' + currentNode.component.kind + '"' : ""}`;
        console.warn(errorMsg);
        continue;
      }
      // Continue traversal if we haven’t seen the relation yet
      if (!visited.has(children.field)) {
        stack.push(children.field);
      }
    }
  }
  return downstreamNotifications;
}

/**
 *  Returns a map: targetField -> tree path to be used for scrolling to the component
 *  via findAndGoToComponent in ConfigSearch.tsx
 */
export function buildTargetFieldPathMap(
  components: DistroComponent[],
  parentTree: string[] = [],
  map: Record<string, string[]> = {}
): Record<string, string[]> {
  console.time('buildTargetFieldPathMap elapsed time:');
  for (const component of components) {
    let blockName = (typeof (component as any).name === 'object'
      ? ((component as any).name?.en || '')
      : ((component as any).name || ''))
      || (typeof (component as any).title === 'object'
        ? ((component as any).title?.en || '')
        : ((component as any).title || ''))
      || ((component as any).path) || ''
      || (component as any).targetField
      || (component as any).targetPrefix;

    const tree = [...parentTree, component.kind + (blockName ? ': ' + blockName : '')];

    if (component.kind !== 'Show Field' && ((component as any).targetField || (component as any).targetPrefix)) {
      map[(component as any).targetField || (component as any).targetPrefix] = tree;
    }

    // Recursively handle children
    if (component.kind === 'Section' || component.kind === 'Collection') {
      buildTargetFieldPathMap(component.components, tree, map);
    }
    if (component.kind === 'Subsurvey') {
      buildTargetFieldPathMap(component.sections, tree, map);
    }
    if (component.kind === 'Conditional Block') {
      buildTargetFieldPathMap(component.components, tree, map);
      if (component.otherwise) {
        buildTargetFieldPathMap(component.otherwise, tree, map);
      }
    }
  }
  console.timeEnd('buildTargetFieldPathMap elapsed time:');
  return map;
}